import { useEffect, useState } from 'react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select } from '@/components/ui/Select'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { CourseMutationPayload } from '@/services/coursesService'
import { programmesService } from '@/services/programmesService'
import { departmentsService } from '@/services/departmentsService'
import { ProgrammeModel } from '@/models/programme.model'
import { Department } from '@/models/department.model'
import { Checkbox } from '@/components/ui/Checkbox'
import { usersService } from '@/services/usersService'
import { UserModel, UserRole } from '@/models/user.model'
import { useAuth } from '@/contexts/AuthContext'

export interface CourseFormState {
  programmeId: string
  departmentId?: string
  title: string
  description?: string
  code: string
  credits?: number
  instructorId?: string
  startDate?: string
  endDate?: string
  level?: string
  semester?: string
  is_active: boolean
}

export interface CourseFormProps {
  initialValues?: Partial<CourseFormState>
  onSubmit: (payload: CourseMutationPayload) => Promise<void> | void
  submitLabel?: string
  isSubmitting?: boolean
}

const semesterOptions = [
  { value: 'first', label: 'First Semester' },
  { value: 'second', label: 'Second Semester' },
]

const levelOptions = ['100', '200', '300', '400', '500', '600']

export function buildCoursePayload(values: CourseFormState): CourseMutationPayload {
  return {
    programmeId: values.programmeId,
    title: values.title,
    description: values.description ?? undefined,
    code: values.code,
    credits: values.credits ?? undefined,
    instructorId: values.instructorId ?? undefined,
    startDate: values.startDate ?? undefined,
    endDate: values.endDate ?? undefined,
    departmentId: values.departmentId ?? undefined,
    level: values.level ?? undefined,
    semester: values.semester ?? undefined,
    is_active: values.is_active,
  }
}

export function CourseForm({
  initialValues,
  onSubmit,
  submitLabel = 'Save Course',
  isSubmitting = false,
}: CourseFormProps) {
  const { user } = useAuth()
  const [programmes, setProgrammes] = useState<ProgrammeModel[]>([])
  const [departments, setDepartments] = useState<Department[]>([])
  const [instructors, setInstructors] = useState<UserModel[]>([])
  const [isLoadingOptions, setIsLoadingOptions] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [useCustomInstructor, setUseCustomInstructor] = useState(false)
  const [customInstructorId, setCustomInstructorId] = useState('')

  const isInstructorUser = user?.role === UserRole.INSTRUCTOR
  const userInstructorId = user?.id ?? undefined
  const shouldAutoAssignInstructor = isInstructorUser && !initialValues?.instructorId

  const [formValues, setFormValues] = useState<CourseFormState>({
    programmeId: initialValues?.programmeId ?? '',
    departmentId: initialValues?.departmentId ?? '',
    title: initialValues?.title ?? '',
    description: initialValues?.description ?? '',
    code: initialValues?.code ?? '',
    credits: initialValues?.credits ?? undefined,
    instructorId: initialValues?.instructorId ?? '',
    startDate: initialValues?.startDate ?? '',
    endDate: initialValues?.endDate ?? '',
    level: initialValues?.level ?? '',
    semester: initialValues?.semester ?? '',
    is_active: initialValues?.is_active ?? true,
  })

  useEffect(() => {
    let mounted = true

    const loadOptions = async () => {
      setIsLoadingOptions(true)
      try {
        const [programmeResponse, departmentResponse] = await Promise.all([
          programmesService.getProgrammes({ requireAuth: true }),
          departmentsService.getDepartments(),
        ])

        if (!mounted) return
        setProgrammes(programmeResponse)
        setDepartments(departmentResponse)

        if (!isInstructorUser) {
          const instructorsResponse = await usersService.getUsersPage({
            role: UserRole.INSTRUCTOR,
            perPage: 100,
          })
          if (!mounted) return
          setInstructors(instructorsResponse.data)
        } else {
          setInstructors([])
        }
      } catch (err) {
        if (!mounted) return
        setError((err as Error).message || 'Unable to load supporting data.')
      } finally {
        if (mounted) setIsLoadingOptions(false)
      }
    }

    loadOptions()

    return () => {
      mounted = false
    }
  }, [isInstructorUser])

  useEffect(() => {
    if (!initialValues) return
    setFormValues((prev) => ({
      ...prev,
      ...initialValues,
    }))

    if (initialValues?.instructorId) {
      if (isInstructorUser) {
        setUseCustomInstructor(false)
        setCustomInstructorId('')
      } else {
        const id = initialValues.instructorId
        const exists = instructors.some((instructor) => instructor.id === id)
        setUseCustomInstructor(!exists)
        setCustomInstructorId(id)
        if (exists) {
          setFormValues((prev) => ({ ...prev, instructorId: id }))
        }
      }
    } else {
      setUseCustomInstructor(false)
    }
  }, [initialValues, instructors, isInstructorUser])

  useEffect(() => {
    if (!shouldAutoAssignInstructor || !userInstructorId) return
    setUseCustomInstructor(false)
    setCustomInstructorId('')
    setFormValues((prev) => {
      if (prev.instructorId === userInstructorId) return prev
      return { ...prev, instructorId: userInstructorId }
    })
  }, [shouldAutoAssignInstructor, userInstructorId])

  const handleChange = <T extends keyof CourseFormState>(key: T, value: CourseFormState[T]) => {
    setFormValues((prev) => ({
      ...prev,
      [key]: value,
    }))
  }

  const disabled = isSubmitting || isLoadingOptions

  return (
    <Card className="border-0 shadow-md">
      <CardContent className="space-y-6 p-6">
        {error && (
          <div className="rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-600">
            {error}
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-medium text-secondary-700">
              Programme <span className="text-primary-600">*</span>
            </label>
            <Select
              value={formValues.programmeId}
              onChange={(event) => handleChange('programmeId', event.target.value)}
              disabled={disabled}
            >
              <option value="">Select programme</option>
              {programmes.map((programme) => (
                <option key={programme.id} value={programme.id}>
                  {programme.title}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-secondary-700">Department</label>
            <Select
              value={formValues.departmentId ?? ''}
              onChange={(event) => handleChange('departmentId', event.target.value || undefined)}
              disabled={disabled}
            >
              <option value="">Select department</option>
              {departments.map((department) => (
                <option key={department.id} value={department.id}>
                  {department.name}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-medium text-secondary-700">
              Course Title <span className="text-primary-600">*</span>
            </label>
            <Input
              value={formValues.title}
              onChange={(event) => handleChange('title', event.target.value)}
              placeholder="e.g., Social Media Strategies"
              disabled={disabled}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-secondary-700">
              Course Code <span className="text-primary-600">*</span>
            </label>
            <Input
              value={formValues.code}
              onChange={(event) => handleChange('code', event.target.value)}
              placeholder="e.g., MKT201"
              disabled={disabled}
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-secondary-700">Description</label>
          <Textarea
            value={formValues.description ?? ''}
            onChange={(event) => handleChange('description', event.target.value)}
            placeholder="Describe the course overview, expectations, and learning outcomes."
            rows={4}
            disabled={disabled}
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-medium text-secondary-700">Credits</label>
            <Input
              type="number"
              min={0}
              value={formValues.credits ?? ''}
              onChange={(event) =>
                handleChange('credits', event.target.value ? Number(event.target.value) : undefined)
              }
              disabled={disabled}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-secondary-700">Instructor</label>
            {isInstructorUser ? (
              <Input
                value={
                  [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
                  user?.email ||
                  userInstructorId ||
                  ''
                }
                disabled
              />
            ) : (
              <>
                <Select
                  value={useCustomInstructor ? '__custom__' : formValues.instructorId ?? ''}
                  onChange={(event) => {
                    const value = event.target.value
                    if (value === '__custom__') {
                      setUseCustomInstructor(true)
                      handleChange('instructorId', customInstructorId || undefined)
                    } else {
                      setUseCustomInstructor(false)
                      setCustomInstructorId('')
                      handleChange('instructorId', value || undefined)
                    }
                  }}
                  disabled={disabled}
                >
                  <option value="">Select instructor</option>
                  {instructors.map((instructor) => (
                    <option key={instructor.id ?? instructor.email ?? ''} value={instructor.id ?? ''}>
                      {([instructor.firstName, instructor.lastName].filter(Boolean).join(' ') ||
                        instructor.email ||
                        instructor.id) ?? 'Instructor'}
                    </option>
                  ))}
                  {/* <option value="__custom__">Use custom instructor ID</option> */}
                </Select>
                {useCustomInstructor && (
                  <Input
                    className="mt-2"
                    value={customInstructorId}
                    onChange={(event) => {
                      const value = event.target.value
                      setCustomInstructorId(value)
                      handleChange('instructorId', value || undefined)
                    }}
                    placeholder="Enter instructor ID"
                    disabled={disabled}
                  />
                )}
              </>
            )}
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-medium text-secondary-700">Start Date</label>
            <Input
              type="date"
              value={formValues.startDate ?? ''}
              onChange={(event) => handleChange('startDate', event.target.value || undefined)}
              disabled={disabled}
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-secondary-700">End Date</label>
            <Input
              type="date"
              value={formValues.endDate ?? ''}
              onChange={(event) => handleChange('endDate', event.target.value || undefined)}
              disabled={disabled}
            />
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-medium text-secondary-700">Level</label>
            <Select
              value={formValues.level ?? ''}
              onChange={(event) => handleChange('level', event.target.value || undefined)}
              disabled={disabled}
            >
              <option value="">Select level</option>
              {levelOptions.map((level) => (
                <option key={level} value={level}>
                  {level} Level
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-secondary-700">Semester</label>
            <Select
              value={formValues.semester ?? ''}
              onChange={(event) => handleChange('semester', event.target.value || undefined)}
              disabled={disabled}
            >
              <option value="">Select semester</option>
              {semesterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-secondary-600">
          <Checkbox
            checked={formValues.is_active}
            onChange={(event) => handleChange('is_active', event.target.checked)}
            disabled={disabled}
          />
          Course is active
        </label>

        <div className="flex justify-end">
          <Button
            type="button"
            className="bg-rose-600 hover:bg-rose-700 text-white"
            disabled={disabled || !formValues.programmeId || !formValues.title || !formValues.code}
            onClick={() => onSubmit(buildCoursePayload(formValues))}
          >
            {isSubmitting ? 'Saving…' : submitLabel}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
